import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import {
  BuildingInterface,
  setBuilding,
  setByFlag,
} from '@store/actionSlices/building'
import { setInteractivePlan } from '@store/actionSlices/interactivePlan'
import {
  setMarkerColour,
  setProjectConfig,
} from '@store/actionSlices/projectConfig'
import { RouteKey } from '@store/constants'
import type { RootStateTypeExtra, SessionMap } from '@store/types'

import Container from '@components/container'
import DataHandler from '@components/data-handler'
import {
  PENDING_STATUS,
  UNINITIALIZED_STATUS,
} from '@components/data-handler/data-handler'
import ImageHandler from '@components/image-handler'
import ProjectSwitchToggle from '@components/project-switch/ProjectSwitchToggle'
import useProjectSwitch from '@components/project-switch/useProjectSwitch'
import { CanvasInteractive } from '@components/showcase-canvas'
import { CanvasRefInterface } from '@components/showcase-canvas/canvas-interactive'
import { MarkerType } from '@components/showcase-canvas/constants'
import { Polygon } from '@components/showcase-canvas/types'

import {
  selectFromResult as selectFromBuildingResult,
  useGetBuildingQuery,
} from '@api/building'
import {
  selectFromResult as selectFromResultConfig,
  useGetConfigQuery,
} from '@api/config'
import {
  InteractivePlanData,
  MapContent,
  selectFromResult,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'
import { ApartmentActiveKeyType } from '@api/types/apartment-type'

import styleUtil from '@utilities/style-util'

import AreaSkeleton from './area-view-skeleton'

export interface AreaViewProps {
  session: SessionMap | undefined
  projectName: string
  interactivePlan: InteractivePlanData
  building: BuildingInterface
  areaMarkerColour: string
}

const ARROW_TYPE = 'arrow'
const ARROW_SMALL_TYPE = 'arrow-small'

function AreaViewApartments({
  session,
  projectName,
  interactivePlan,
  building,
  areaMarkerColour,
}: AreaViewProps) {
  const themeData = styleUtil()
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()

  const [isCanvasImageLoaded, setImageLoaded] = useState(false)

  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<CanvasRefInterface>()
  const [renderCanvas, setRenderCanvas] = useState(false)
  const [theme, setTheme] = useState({
    font: '',
    mainColour: '',
  })
  const [isConnected, setIsConnected] = useState(false)

  const interactiveQueryResult = useGetInteractivePlanQuery(
    { projectName },
    { selectFromResult }
  )

  const buildingQueryResult = useGetBuildingQuery(
    { projectName },
    { selectFromResult: selectFromBuildingResult }
  )

  const { configInfo, markerColour, isLoaded } = useGetConfigQuery(
    { projectName },
    { selectFromResult: selectFromResultConfig }
  )

  useEffect(() => {
    if (configInfo && Object.keys(configInfo).length > 0) {
      dispatch(setProjectConfig({ ...configInfo }))
    }
  }, [isLoaded])

  useEffect(() => {
    if (markerColour && Object.keys(markerColour).length > 0) {
      dispatch(setMarkerColour(markerColour))
    }
  }, [isLoaded])

  const getLabel = (poly: Polygon) => {
    const POLY_TYPE = poly.type || ''
    if (
      POLY_TYPE === ARROW_TYPE ||
      POLY_TYPE === ARROW_SMALL_TYPE ||
      POLY_TYPE === MarkerType.oblongLabel ||
      POLY_TYPE === MarkerType.oblongStatus
    ) {
      return poly.label || poly.groupId
    }
    return poly.label
  }

  const setInteractiveAction = (areaViewMap: MapContent) => {
    const areaData = {
      ...areaViewMap,
      polygons: areaViewMap.polygons.map((poly) => ({
        ...poly,
        label: getLabel(poly),
        onClick: () => {
          const blockId = building?.blockOrders?.includes(poly.groupId)
            ? poly.groupId
            : building?.blockOrders?.[0]
          dispatch(
            setByFlag({
              flag: ApartmentActiveKeyType.ActiveBlock,
              value: blockId,
            })
          )
          if (!isConnected) {
            history.push(RouteKey.LEVEL_VIEW)
          }
        },
        markerColour: areaMarkerColour,
      })),
    }
    return areaData
  }

  const area = useMemo(() => {
    if (interactivePlan.areaView?.image) {
      return setInteractiveAction(interactivePlan.areaView)
    }
    return {
      image: '',
      polygons: [],
    }
  }, [interactivePlan, isConnected, building?.blockOrders])

  useEffect(() => {
    setRenderCanvas(true)
  }, [])

  const redirectToLevelView = useCallback(() => {
    if (isConnected) {
      return
    }

    const { maps } = interactiveQueryResult

    dispatch(
      setByFlag({
        flag: ApartmentActiveKeyType.ActiveBlock,
        value:
          Object.keys(interactivePlan.blocks || {})?.[0] ||
          Object.keys(maps.blocks || {})?.[0] ||
          '',
      })
    )
    history.push(RouteKey.LEVEL_VIEW)
  }, [isConnected, interactiveQueryResult])

  const shouldRedirectToLevelView = useMemo(() => {
    const { maps } = interactiveQueryResult

    const isInteractiveQueryLoaded =
      interactiveQueryResult.isLoaded &&
      interactiveQueryResult.status !== UNINITIALIZED_STATUS &&
      interactiveQueryResult.status !== PENDING_STATUS
    const hasNoAreaViewImage = !maps?.areaView?.image
    const hasBlocks =
      Object.keys(interactivePlan.blocks || {}).length > 0 ||
      Object.keys(maps.blocks || {}).length > 0

    return isInteractiveQueryLoaded && hasNoAreaViewImage && hasBlocks
  }, [interactivePlan, interactiveQueryResult])

  useEffect(() => {
    if (shouldRedirectToLevelView) {
      redirectToLevelView()
    }
  }, [shouldRedirectToLevelView, location, redirectToLevelView])

  useEffect(() => {
    const { maps } = interactiveQueryResult
    if (!interactivePlan.areaView?.image && maps.areaView) {
      dispatch(setInteractivePlan(maps))
    }
  }, [interactiveQueryResult, interactivePlan])

  useEffect(() => {
    const { building: buildingData, blockOrders: blockOrdersData } =
      buildingQueryResult
    if (building.levels.length === 0 && buildingData.length > 0) {
      dispatch(
        setBuilding({
          ...building,
          blockOrders: blockOrdersData,
          levels: buildingData,
          activeLevel: buildingData[0]?.level,
        })
      )
    }
  }, [buildingQueryResult, building])

  useEffect(() => {
    if (session) {
      const {
        connected,
        areaView: { triggerBuilding },
        building: { activeBlock },
      } = session
      if (activeBlock) {
        dispatch(
          setByFlag({
            flag: ApartmentActiveKeyType.ActiveBlock,
            value: activeBlock,
          })
        )
      }

      if (triggerBuilding && renderCanvas) {
        let polyToTrigger: string | undefined =
          area?.polygons[0]?.groupId || 'BuildingGroup'
        if (activeBlock) {
          const foundItem = area?.polygons.find(
            (res) => activeBlock === res.groupId
          )?.groupId
          if (foundItem) {
            polyToTrigger = foundItem
          }
        }
        canvasRef?.current?.artificialTrigger(polyToTrigger || 'BuildingGroup')
      }
      setIsConnected(connected)
    }
  }, [session])

  useEffect(() => {
    const themeFromStorage = JSON.parse(
      localStorage.getItem('themeObject') || '{}'
    )
    if (themeFromStorage) {
      setTheme(themeFromStorage)
    }
  }, [])

  const projectSwitch = useProjectSwitch()

  return (
    <Container>
      <DataHandler
        payloads={[
          {
            name: 'interactiveQueryResult',
            payload: {
              ...interactiveQueryResult,
              data: interactivePlan?.areaView?.image,
            },
          },
          {
            name: 'interactiveQueryResult',
            payload: {
              ...buildingQueryResult,
              data: building.levels,
            },
          },
        ]}
        showDefaultNotFound={!shouldRedirectToLevelView}
        skeletonFrame={<AreaSkeleton />}
      >
        {area.polygons.length > 0 ? (
          <>
            <ImageHandler
              key={area.image}
              url={area.image}
              type="new"
              className="background-cover image-blur absolute inset-0 z-2"
              noSpliceUrl
              showFallbackImage={false}
              preferCache
              bgProps={{
                gradiant: 0.5,
              }}
            />
            <div className="absolute inset-0 z-3">
              <div
                key="area-canvas"
                data-testid="area-canvas"
                ref={canvasContainerRef}
                className={`relative flex h-full w-full items-center justify-center overflow-hidden ${
                  isCanvasImageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <CanvasInteractive
                  ref={canvasRef}
                  id="area-canvas"
                  canvasData={area}
                  parentRef={canvasContainerRef}
                  theme={{
                    brandColour:
                      theme?.mainColour || themeData.mainColour || '',
                    font: theme?.font || themeData.font || '',
                  }}
                  adjustCanvasSizeWithContainer
                  setImageLoaded={setImageLoaded}
                />
              </div>
            </div>
          </>
        ) : (
          <AreaSkeleton />
        )}
        {projectSwitch.enable && !session?.connected && (
          <ProjectSwitchToggle
            className="absolute bottom-5 left-5 z-3"
            isPanelVisible={projectSwitch.isPanelVisible}
            isProcessing={projectSwitch.isProcessing}
            projects={projectSwitch.projectGroup}
            handleTogglePanel={projectSwitch.handleTogglePanel}
            handleProjectSelect={projectSwitch.handleProjectSwitch}
            showOverlayOnProcessing
          />
        )}
      </DataHandler>
    </Container>
  )
}

export default connect(
  ({
    firestore: { session },
    projectIdentity: { projectName },
    interactivePlan,
    building,
    projectConfig: {
      markerColour: { area: areaMarkerColour },
    },
  }: RootStateTypeExtra) => ({
    session,
    projectName,
    interactivePlan,
    building,
    areaMarkerColour,
  })
)(AreaViewApartments)
